import { createWithEqualityFn } from "zustand/traditional";
import { shallow } from "zustand/shallow";
import { getConfig, getInitData } from "../../api";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import type { ConfigInterface } from "../types";
import { useSecuritySetting } from "../store";

const TELEMETRY_SESSION_KEY = "app-frontend-telemetry-session-id";

const getTelemetrySessionId = (): string => {
    try {
        const existingSessionId = window.sessionStorage.getItem(TELEMETRY_SESSION_KEY);
        if (existingSessionId) {
            return existingSessionId;
        }

        const nextSessionId = crypto.randomUUID();
        window.sessionStorage.setItem(TELEMETRY_SESSION_KEY, nextSessionId);
        return nextSessionId;
    } catch {
        return "session-unavailable";
    }
};

const getBrowserContext = () => ({
    browserLanguage: navigator.language,
    browserPlatform: (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ?? navigator.platform,
    browserUserAgent: navigator.userAgent,
});


export const useConfigStore = createWithEqualityFn<ConfigInterface>()(
    set => ({
        language: "",
        production: false,
        initialized: false,
        initializationFailed: false,
        appInsights: undefined,
        reactPlugin: undefined,
        speechEnabled: false,
        streamingEnabled: false,
        faqUploadEnabled: false,
        translations: {},
        fileSchema: {},
        url: "",
        tutorialUrl: "",
        speechSetup: "simple",
        metadataDefinitionsEnabled: false,
        init: async () => {
            console.log("Store: Loading config");
            try {
                const response = await getConfig();
                const initRequest = await getInitData();
                const reactPlugin = new ReactPlugin();
                const telemetryConnectionString = response.app_insights_url.trim();
                const telemetrySessionId = getTelemetrySessionId();
                const browserContext = getBrowserContext();

                const appInsights = new ApplicationInsights({
                    config: {
                        connectionString: telemetryConnectionString,
                        autoTrackPageVisitTime: true,
                        enableCorsCorrelation: true,
                        enableUnhandledPromiseRejectionTracking: true,
                        extensions: [reactPlugin],
                    }
                });

                if (telemetryConnectionString !== "") {
                    appInsights.addTelemetryInitializer((envelope) => {
                        const baseData = envelope.baseData as {
                            properties?: Record<string, string>;
                        } | undefined;

                        if (baseData) {
                            baseData.properties = {
                                ...baseData.properties,
                                appEnvironment: response.production ? "production" : "development",
                                frontendSessionId: telemetrySessionId,
                                ...browserContext,
                            };
                        }

                        return true;
                    });

                    appInsights.loadAppInsights();
                }

                set({
                    language: response.language === "es_va" ? "es" : response.language,
                    production: response.production,
                    speechEnabled: response.speech_2_text_enabled,
                    streamingEnabled: response.streaming_enabled,
                    faqUploadEnabled: response.faq_upload_enabled,
                    appInsights: appInsights,
                    reactPlugin: reactPlugin,
                    initialized: true,
                    url: response.url,
                    tutorialUrl: response.tutorial_url,
                    speechSetup: response.speech_setup,
                    translations: initRequest.translation,
                    fileSchema: initRequest.file_schema,
                    metadataDefinitionsEnabled: response.metadata_definitions_enabled ?? false,
                });
                useSecuritySetting.getState().init(response);
            } catch (error) {
                console.error("Error when initializing configuration:", error);
                set({ initializationFailed: true });
                return;
            }
        },
    }),
    shallow
);
